"use client";

import { useState, useCallback, useRef } from "react";
import { usePathname } from "next/navigation";
import { buildPageContext, getAIConfig, selectProvider, estimateComplexity } from "@/services/aiService";
import type { PageContext, AIActionType } from "@/services/aiService";
import {
  AI_CHAT_FETCH_ABORT_MS,
  AI_CHAT_HARD_STOP_MS,
  aiChatTimeoutMessage,
} from "@/lib/ai-chat-timeouts";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  action?: AIActionType;
  isStreaming?: boolean;
}

export function useAIChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const pathname = usePathname();

  const pageContext = buildPageContext(pathname);

  const sendMessage = useCallback(
    async (content: string, extraContext?: Record<string, unknown>) => {
      if (!content.trim() || isLoading) return;

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: content.trim(),
        timestamp: Date.now(),
      };

      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: "",
        timestamp: Date.now(),
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsLoading(true);

      const config = getAIConfig();
      const complexity = estimateComplexity(content);
      const provider = selectProvider(config, complexity);

      if (provider === "disabled") {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? { ...m, content: "L'IA est désactivée. Activez-la dans les paramètres.", isStreaming: false }
              : m
          )
        );
        setIsLoading(false);
        return;
      }

      const ctx: PageContext = {
        ...pageContext,
        data: { ...pageContext.data, ...extraContext },
      };

      const apiMessages = messages
        .concat(userMsg)
        .map((m) => ({ role: m.role, content: m.content }));

      abortRef.current = new AbortController();
      const timeoutMsg = aiChatTimeoutMessage();
      const timeout = setTimeout(() => abortRef.current?.abort(), AI_CHAT_FETCH_ABORT_MS);
      const hardTimeout = setTimeout(() => {
        abortRef.current?.abort();
        setIsLoading(false);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id && m.isStreaming
              ? { ...m, content: m.content || timeoutMsg, isStreaming: false }
              : m
          )
        );
      }, AI_CHAT_HARD_STOP_MS);

      try {
        const res = await fetch("/api/ai/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: apiMessages,
            pageContext: ctx,
            provider,
            stream: true,
          }),
          signal: abortRef.current.signal,
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: "Erreur serveur" }));
          throw new Error(err.error || `HTTP ${res.status}`);
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error("Pas de stream");

        const decoder = new TextDecoder();
        let fullContent = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const data = line.slice(6);
            if (data === "[DONE]") continue;

            try {
              const parsed = JSON.parse(data);
              if (parsed.token) {
                fullContent += parsed.token;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsg.id ? { ...m, content: fullContent } : m
                  )
                );
              }
              if (parsed.error) {
                throw new Error(parsed.error);
              }
            } catch {
              // skip unparseable lines
            }
          }
        }

        // Parse action from response
        let action: AIActionType | undefined;
        const actionMatch = fullContent.match(/```action\s*\n([\s\S]*?)\n```/);
        if (actionMatch) {
          try {
            action = JSON.parse(actionMatch[1]);
            fullContent = fullContent.replace(/```action\s*\n[\s\S]*?\n```/, "").trim();
          } catch {
            // ignore
          }
        }

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? { ...m, content: fullContent, isStreaming: false, action }
              : m
          )
        );
      } catch (err: any) {
        const errorMsg = err.name === "AbortError" ? timeoutMsg : `Erreur : ${err.message}`;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? { ...m, content: errorMsg, isStreaming: false }
              : m
          )
        );
      } finally {
        clearTimeout(timeout);
        clearTimeout(hardTimeout);
        setIsLoading(false);
        abortRef.current = null;
      }
    },
    [messages, isLoading, pageContext]
  );

  const stopGeneration = useCallback(() => {
    abortRef.current?.abort();
    setIsLoading(false);
    setMessages((prev) =>
      prev.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m))
    );
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    isLoading,
    sendMessage,
    stopGeneration,
    clearMessages,
    pageContext,
  };
}
